// Fichier: /pages/Home.tsx (ou équivalent)
// Version avec fond du contenu assombri, bannière intacte.

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    ArrowRight, 
    ShieldCheck, 
    Wrench,
    Code,
    Headset,
    MapPin,
    Clock,
    Settings2,
    Sparkles,
    CheckCircle,
    HeartHandshake,
    Network,
    Server
} from 'lucide-react';

import { PublicLayout } from '../components/layout/PublicLayout';
import { SEOMetaTags } from '../components/shared/SEOMetaTags';
import { useTenantContext } from '../context/TenantContext';

// --- PALETTE DE COULEURS SIS ---
// Bleu Nuit (nouveau fond contenu): #08062b
// Bleu Foncé (utilisé dans bannière): #100e4d
// Orange (accent 1): #fe8d17
// Bleu Ciel (accent 2): #008aef


// --- COMPOSANT ANIMÉ POUR LES CHIFFRES ---
const AnimatedCounter = ({
    target,
    suffix = "",
    icon: Icon,
    label,
    delay = 0,
    color = "#fe8d17"
}: {
    target: number;
    suffix?: string;
    icon: React.ComponentType<any>;
    label: string;
    delay?: number;
    color?: string;
}) => {
    const [count, setCount] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting && !isVisible) setIsVisible(true);
        }, { threshold: 0.3 });
        if (ref.current) observer.observe(ref.current);
        return () => {
            if (ref.current) observer.disconnect();
        };
    }, [isVisible, ref]);

    useEffect(() => {
        if (isVisible) {
            const startTime = Date.now();
            const duration = 2500;
            const animate = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const easeOutQuart = 1 - Math.pow(1 - progress, 4);
                const currentVal = Math.floor(easeOutQuart * target);
                setCount(currentVal);
                if (progress < 1) {
                    requestAnimationFrame(animate);
                } else {
                    setCount(target);
                }
            };
            setTimeout(() => requestAnimationFrame(animate), delay);
        }
    }, [isVisible, target, delay]);

    return (
        <div ref={ref} className="bg-white/5 p-6 rounded-lg backdrop-blur-sm relative overflow-hidden group hover:scale-105 transition-all duration-500 border border-white/10">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative z-10 text-center">
                <Icon className="w-10 h-10 mx-auto mb-3 drop-shadow-lg transition-colors duration-300" style={{ color: color }}/>
                <div className="text-4xl font-bold text-white drop-shadow-md transition-colors duration-300">
                    {count}{suffix}
                </div>
                <div className="text-white/70 group-hover:text-white/90 transition-colors duration-300">{label}</div>
            </div>
        </div>
    );
};


// --- SECTION 1: HERO BANNER (INCHANGÉE) ---
const heroContent = [
    {
        tag: "Solutions Informatiques complètes",
        title: "Votre partenaire technologique pour une croissance accélérée.",
        description: "De la maintenance à l'infrastructure réseau, en passant par le développement web, SIS vous fournit des solutions fiables et innovantes adaptées à vos besoins au Congo.",
        image: "https://img.freepik.com/photos-premium/ingenieur-reseau-utilisant-ordinateur-portable-dans-salle-serveurs_236854-41049.jpg?semt=ais_hybrid&w=740&q=80"
    },
    {
        tag: "Support & Maintenance Proactifs",
        title: "Gardez vos systèmes opérationnels, sans interruption.",
        description: "Notre équipe d'experts assure un dépannage rapide et une maintenance préventive pour garantir la performance et la sécurité de votre parc informatique.",
        image: "https://www.lemoci.com/wp-content/uploads/2022/03/Informatique2-gorodenkoff-iStock-750x536.jpg"
    },
];

const HeroSection = () => {
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % heroContent.length);
        }, 6000);
        return () => clearInterval(timer);
    }, []);

    const currentItem = heroContent[currentIndex];

    return (
        <section className="min-h-screen flex items-center justify-center text-center relative overflow-hidden bg-[#100e4d]">
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentIndex}
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${currentItem.image})` }}
                    initial={{ opacity: 0, scale: 1.1 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.05 }}
                    transition={{ duration: 1.5, ease: 'easeInOut' }}
                />
            </AnimatePresence>

            <div className="absolute inset-0 bg-gradient-to-br from-[#100e4d]/90 via-[#100e4d]/70 to-black/60"></div>
            <div className="absolute inset-0 bg-black/40 backdrop-blur-[3px]"></div>

            <div className="relative z-10 container mx-auto px-4">
                 <motion.div key={`${currentIndex}-tag`} initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
                    <div className="inline-flex items-center gap-2 bg-[#008aef]/20 backdrop-blur-sm border border-[#008aef]/30 rounded-full px-4 py-2 mb-6">
                        <Sparkles className="w-4 h-4 text-[#008aef]" />
                        <span className="text-[#008aef] font-medium text-sm">{currentItem.tag}</span>
                    </div>
                </motion.div>

                <motion.h1 key={`${currentIndex}-title`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4, type: "spring" }} className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 drop-shadow-2xl max-w-4xl mx-auto">
                    {currentItem.title}
                </motion.h1>
                <motion.p key={`${currentIndex}-desc`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6 }} className="text-white/80 text-lg md:text-xl max-w-3xl mx-auto mb-10 leading-relaxed">
                    {currentItem.description}
                </motion.p>
                <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.8 }} className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <a href="#services" className="bg-gradient-to-r from-[#008aef] to-[#007acc] hover:from-[#007acc] hover:to-[#006abb] text-white font-bold py-4 px-8 rounded-xl text-lg inline-flex items-center transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-[#008aef]/25">
                        <CheckCircle className="w-5 h-5 mr-3" />
                        Découvrir nos services
                    </a>
                    <a href="/contact" className="bg-[#fe8d17] hover:bg-[#e47c15] text-white font-bold py-4 px-8 rounded-xl text-lg inline-flex items-center transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-[#fe8d17]/25">
                        <Headset className="w-5 h-5 mr-3" />
                        Demander un devis
                    </a>
                </motion.div>
            </div>
        </section>
    );
};


// --- SECTION 2: PRÉSENTATION & STATS ---
const IntroAndStatsSection = () => (
    <section className="py-20 bg-white/5">
        <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
                <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                        Votre partenaire de confiance pour une <span className="text-[#008aef]">transformation</span> <span className="text-[#fe8d17]">numérique</span> réussie
                    </h2>
                    <p className="text-white/80 leading-relaxed mb-8">
                        Chez SIS, nous comprenons les défis technologiques uniques des entreprises au Congo. Notre mission est de fournir des services informatiques fiables, sécurisés et performants pour accompagner votre développement et garantir votre sérénité.
                    </p>
                    <a href="/a-propos" className="inline-flex items-center gap-2 text-white hover:text-[#fe8d17] font-medium py-2 px-4 rounded-md border border-white/30 hover:border-[#fe8d17]/60 hover:bg-[#fe8d17]/10 transition-all duration-300 group text-sm">
                        <span>En savoir plus sur notre mission</span>
                        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                    </a>
                </motion.div>
                <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.2 }} className="grid grid-cols-2 gap-6">
                   <AnimatedCounter target={50} suffix="+" icon={HeartHandshake} label="Clients Satisfaits" color="#fe8d17" />
                   <AnimatedCounter target={250} suffix="+" icon={CheckCircle} label="Projets Livrés" color="#008aef" delay={200} />
                   <AnimatedCounter target={99} suffix="%" icon={ShieldCheck} label="Taux de Disponibilité" color="#008aef" delay={400} />
                   <AnimatedCounter target={24} suffix="/7" icon={Headset} label="Support Technique" color="#fe8d17" delay={600} />
                </motion.div>
            </div>
        </div>
    </section>
);


// --- SECTION 3: PRÉSENTATION DES SERVICES ---
const ServiceShowcase = ({ icon: Icon, title, children, imageUrl, reverse = false, color }: {
    icon: React.ComponentType<any>,
    title: string,
    children: React.ReactNode,
    imageUrl: string,
    reverse?: boolean,
    color: string
}) => (
    <div className="grid lg:grid-cols-2 gap-12 items-center py-12">
        <motion.div initial={{ opacity: 0, x: reverse ? 50 : -50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.8 }} className={`rounded-2xl overflow-hidden shadow-2xl ${reverse ? 'lg:order-last' : ''}`}>
            <img src={imageUrl} alt={title} className="w-full h-full object-cover"/>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.5 }} transition={{ duration: 0.8, delay: 0.2 }}>
            <div className="inline-flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}1A` }}>
                    <Icon className="w-7 h-7" style={{ color: color }} />
                </div>
                <h3 className="text-3xl font-bold text-white">{title}</h3>
            </div>
            <div className="text-white/80 leading-relaxed space-y-4">{children}</div>
        </motion.div>
    </div>
);

const ServicesSection = () => (
    <section id="services" className="py-10">
        <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                    Des services conçus pour votre <span className="text-[#fe8d17]">croissance</span>
                </h2>
                <p className="text-white/80 text-lg max-w-3xl mx-auto">
                    Nous offrons une gamme complète de services pour répondre à tous vos besoins technologiques.
                </p>
            </motion.div>

            <div className="space-y-16">
                <ServiceShowcase title="Support & Maintenance" icon={Wrench} imageUrl="https://img.freepik.com/photos-gratuite/gros-plan-homme-travaillant-puces-informatiques_23-2150880924.jpg?semt=ais_hybrid&w=740&q=80" color="#fe8d17">
                    <p>Assurez la continuité de vos opérations grâce à notre support technique réactif et nos plans de maintenance préventive.</p>
                    <ul className="list-disc list-inside space-y-2 pl-2">
                        <li>Dépannage rapide sur site et à distance (PC/Mac).</li>
                        <li>Installation et configuration de postes de travail.</li>
                        <li>Nettoyage de virus/malwares et optimisation des systèmes.</li>
                    </ul>
                </ServiceShowcase>

                <ServiceShowcase title="Réseaux & Infrastructure" icon={Network} imageUrl="https://img.freepik.com/photos-premium/femme-travaillant-ordinateur-portable-au-milieu-supports-serveurs-dans-centre-donnees_734334-16654.jpg?semt=ais_hybrid&w=740&q=80" reverse color="#008aef">
                    <p>Nous concevons et déployons des infrastructures réseau robustes, sécurisées et évolutives pour votre entreprise.</p>
                     <ul className="list-disc list-inside space-y-2 pl-2">
                        <li>Installation et gestion de réseaux LAN/Wi-Fi.</li>
                        <li>Mise en place et administration de serveurs.</li>
                        <li>Configuration de firewalls et solutions de sécurité VPN.</li>
                    </ul>
                </ServiceShowcase>

                <ServiceShowcase title="Développement & Web" icon={Code} imageUrl="https://images.pexels.com/photos/546819/pexels-photo-546819.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2" color="#fe8d17">
                    <p>Votre présence en ligne est cruciale. Nous créons des sites web modernes et des applications qui captivent vos clients.</p>
                     <ul className="list-disc list-inside space-y-2 pl-2">
                        <li>Création de sites vitrine et e-commerce sur mesure.</li>
                        <li>Développement d’applications simples et intuitives.</li>
                        <li>Maintenance et mise à jour de vos plateformes web.</li>
                    </ul>
                </ServiceShowcase>
            </div>
        </div>
    </section>
);


// --- SECTION 4: POURQUOI NOUS CHOISIR ? ---
const WhyChooseUsSection = () => {
    const features = [
        { icon: MapPin, title: "Expertise locale", desc: "Basés au Congo, nous comprenons vos réalités et intervenons rapidement à Brazzaville et Pointe-Noire.", color: "#fe8d17" },
        { icon: Clock, title: "Support réactif", desc: "Notre équipe est disponible pour résoudre vos problèmes urgents et minimiser votre temps d'arrêt.", color: "#008aef" },
        { icon: Settings2, title: "Solutions sur mesure", desc: "Nous écoutons vos besoins pour vous proposer des solutions parfaitement adaptées à votre budget et vos objectifs.", color: "#fe8d17" }
    ];

    return (
        <section className="py-20 bg-white/5">
            <div className="container mx-auto px-4">
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">L'Avantage <span className="text-[#008aef]">SIS</span> <span className="text-[#fe8d17]">Congo</span></h2>
                    <p className="text-white/80 text-lg max-w-3xl mx-auto">Nous sommes plus qu'un prestataire. Nous sommes votre partenaire de confiance.</p>
                </motion.div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {features.map((feature, i) => (
                        <motion.div key={feature.title} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.15 }} className="bg-white/5 p-8 rounded-xl text-center border border-transparent hover:border-white/20 transition-all duration-300">
                            <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4" style={{ backgroundColor: `${feature.color}1A` }}>
                                <feature.icon className="w-8 h-8" style={{ color: feature.color }}/>
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                            <p className="text-white/70 leading-relaxed">{feature.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};


// --- COMPOSANT PRINCIPAL DE LA PAGE ---
export const Home: React.FC = () => {
    const { tenant } = useTenantContext();

    return (
        <PublicLayout>
            <SEOMetaTags
                title="SIS Congo - Solutions Informatiques pour Entreprises"
                description="SIS fournit des services informatiques complets au Congo : support & maintenance, réseaux, cloud, développement web. Contactez-nous pour une solution sur mesure à Brazzaville et Pointe-Noire."
                url="https://www.sis-cg.com"
                image="https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg"
            />
            <div className="relative">
                <main>
                    <HeroSection />

                    {/* Fond et effets alignés sur About */}
                    <div className="min-h-screen relative overflow-hidden">
                        {/* Image de fond fixe + overlay */}
                        <div 
                            className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
                            style={{
                                backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url()',
                                backgroundAttachment: 'fixed'
                            }}
                        >
                            <div className="absolute inset-0 bg-gradient-to-b from-[#08062b]/95 via-[#08062b]/80 to-[#08062b]/95 backdrop-blur-[2px]"></div>
                        </div>

                        {/* Particules et décorations */}
                        <div className="absolute inset-0 overflow-hidden z-10 pointer-events-none">
                            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#fe8d17]/8 rounded-full blur-3xl animate-pulse" />
                            <div className="absolute top-3/4 right-1/4 w-96 h-96 bg-[#008aef]/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
                            <div className="absolute top-1/2 left-3/4 w-48 h-48 bg-white/5 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '4s' }} />
                        </div>
                        <div className="absolute top-20 right-10 opacity-15 z-10 pointer-events-none">
                            <Wrench className="w-16 h-16 text-[#008aef] animate-spin" style={{ animationDuration: '20s' }} />
                        </div>
                        <div className="absolute bottom-20 left-10 opacity-15 z-10 pointer-events-none">
                            <Server className="w-12 h-12 text-white/10 animate-bounce" />
                        </div>

                        {/* Contenu des sections */}
                        <div className="relative z-10">
                            <IntroAndStatsSection />
                            <ServicesSection />
                            <WhyChooseUsSection />
                        </div>
                    </div>
                </main>
            </div>
        </PublicLayout>
    );
};